import { formatPesoAmount } from '../utils/money';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createProviderPayment,
  deleteProviderPayment,
  getProvider,
  getProviderOpenStocks,
  getProviderPayments,
  getProviderSummary,
  updateProviderPayment,
  uploadFile,
} from "../api";
import InlineLoading from "../components/InlineLoading";
import ProviderPaymentModal from "../components/providers/ProviderPaymentModal";
import useCurrentUser from "../hooks/useCurrentUser";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { normalizePaginatedResponse } from "../utils/pagination";
import { hasPermission } from "../utils/permissions";
import {
  getProviderApiErrorMessage,
  getProviderPaymentMethodLabel,
  PROVIDER_PAYMENT_MAX_AMOUNT,
  PROVIDER_PAYMENT_METHODS,
  PROVIDER_TEXT_MAX_LENGTH,
  validateProviderPaymentForm,
} from "../utils/provider";
import { formatRut } from "../utils/rut";

const paymentMethods = PROVIDER_PAYMENT_METHODS.map((method) => ({
  id: method,
  label: getProviderPaymentMethodLabel(method),
}));

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const formatMoney = (value) => formatPesoAmount(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("es-CL");
};

const toLocalDateKey = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildStockListUrl = (stockList) => {
  if (!stockList?.id || !stockList?.branchId) return "";
  return `/branches/${stockList.branchId}/stock/${stockList.id}`;
};

const getAllocatedAmount = (payment) =>
  (payment?.ProviderPaymentAllocations || []).reduce(
    (sum, allocation) => sum + Number(allocation?.amount || 0),
    0
  );

const ProviderDetailPage = () => {
  const navigate = useNavigate();
  const { branchId, id } = useParams();
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();

  const [provider, setProvider] = useState(null);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerError, setProviderError] = useState("");

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");

  const [openStocks, setOpenStocks] = useState([]);
  const [openStocksLoading, setOpenStocksLoading] = useState(true);
  const [openStocksError, setOpenStocksError] = useState("");

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState("");

  const [paymentError, setPaymentError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [expandedPaymentIds, setExpandedPaymentIds] = useState(() => new Set());
  const [paymentFilters, setPaymentFilters] = useState({
    from: "",
    to: "",
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    method: "",
    reference: "",
    paidAt: getTodayDateString(),
    note: "",
    voucherFile: null,
  });

  const canUpdateProvider = hasPermission(user, "provider.update");
  const canManageExistingPayments =
    canUpdateProvider && hasPermission(user, "branch.access.any");

  const fetchProviderProfile = useCallback(async () => {
    setProviderLoading(true);
    setProviderError("");
    try {
      const data = await getProvider(id, { includeDeleted: true });
      setProvider(data);
    } catch (err) {
      console.error(err);
      setProvider(null);
      setProviderError(
        getProviderApiErrorMessage(err, "Error al cargar la informacion del proveedor.")
      );
    } finally {
      setProviderLoading(false);
    }
  }, [id]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError("");
    try {
      const data = await getProviderSummary(id);
      setSummary(data);
    } catch (err) {
      console.error(err);
      setSummary(null);
      setSummaryError(getProviderApiErrorMessage(err, "Error al cargar el resumen del proveedor."));
    } finally {
      setSummaryLoading(false);
    }
  }, [id]);

  const fetchOpenStocks = useCallback(async () => {
    setOpenStocksLoading(true);
    setOpenStocksError("");
    try {
      const data = await getProviderOpenStocks(id, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setOpenStocks(normalized.data || []);
    } catch (err) {
      console.error(err);
      setOpenStocks([]);
      setOpenStocksError(
        getProviderApiErrorMessage(err, "Error al cargar los stocks pendientes.")
      );
    } finally {
      setOpenStocksLoading(false);
    }
  }, [id]);

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    setPaymentsError("");
    try {
      const data = await getProviderPayments(id, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      const nextPayments = normalized.data || [];
      setPayments(nextPayments);
      setExpandedPaymentIds((prev) => {
        const next = new Set();
        nextPayments.forEach((payment) => {
          if (prev.has(payment.id)) {
            next.add(payment.id);
          }
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      setPayments([]);
      setPaymentsError(getProviderApiErrorMessage(err, "Error al cargar el historial de pagos."));
    } finally {
      setPaymentsLoading(false);
    }
  }, [id]);

  const fetchPageData = useCallback(async () => {
    await Promise.allSettled([
      fetchProviderProfile(),
      fetchSummary(),
      fetchOpenStocks(),
      fetchPayments(),
    ]);
  }, [fetchOpenStocks, fetchPayments, fetchProviderProfile, fetchSummary]);

  const refreshPaymentData = useCallback(async () => {
    await Promise.allSettled([fetchSummary(), fetchOpenStocks(), fetchPayments()]);
  }, [fetchOpenStocks, fetchPayments, fetchSummary]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const handlePaymentChange = (field, value) => {
    setPaymentForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTogglePaymentExpansion = (paymentId) => {
    setExpandedPaymentIds((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  const handleSubmitPayment = async () => {
    if (!canUpdateProvider) return;
    setPaymentError("");
    const { amount, error: validationError } = validateProviderPaymentForm(paymentForm);
    if (validationError) {
      setPaymentError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      let voucherUrl;
      if (paymentForm.voucherFile) {
        const response = await uploadFile(paymentForm.voucherFile);
        if (!response?.url) {
          throw new Error("No se pudo obtener la URL del comprobante.");
        }
        voucherUrl = response?.url;
      }

      const payload = {
        amount,
        method: paymentForm.method,
        reference: paymentForm.reference || undefined,
        paidAt: paymentForm.paidAt || undefined,
        note: paymentForm.note || undefined,
      };
      if (voucherUrl) {
        payload.voucherUrl = voucherUrl;
      }

      await createProviderPayment(id, payload);
      setPaymentForm({
        amount: "",
        method: "",
        reference: "",
        paidAt: getTodayDateString(),
        note: "",
        voucherFile: null,
      });
      await refreshPaymentData();
    } catch (err) {
      console.error(err);
      setPaymentError(getProviderApiErrorMessage(err, "Error al registrar el pago."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePayment = async (payload) => {
    if (!editingPayment) return;
    await updateProviderPayment(id, editingPayment.id, payload);
    setEditingPayment(null);
    await refreshPaymentData();
  };

  const handleDeletePayment = async (payment) => {
    if (!canManageExistingPayments) return;
    const approved = await confirm({
      title: "Eliminar pago",
      message: `Eliminar el pago #${payment.id}? Esta accion recalculara las asignaciones del proveedor.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!approved) return;

    setDeletingPaymentId(payment.id);
    setPaymentsError("");
    try {
      await deleteProviderPayment(id, payment.id);
      if (editingPayment?.id === payment.id) {
        setEditingPayment(null);
      }
      await refreshPaymentData();
    } catch (err) {
      console.error(err);
      setPaymentsError(getProviderApiErrorMessage(err, "Error al eliminar el pago."));
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const amountValue = Number(paymentForm.amount || 0);
  const currentBalance = Number(summary?.balance || 0);
  const pendingBalance = Math.max(currentBalance, 0);
  const remaining = Math.max(pendingBalance - amountValue, 0);
  const overpay = amountValue > pendingBalance ? amountValue - pendingBalance : 0;

  const allocationPreview = useMemo(() => {
    let remainingAmount = amountValue;
    if (!Number.isFinite(remainingAmount) || remainingAmount <= 0) return [];
    const allocations = [];
    openStocks.forEach((stock) => {
      if (remainingAmount <= 0) return;
      const stockBalance = Number(stock.balance || 0);
      if (stockBalance <= 0) return;
      const applied = Math.min(stockBalance, remainingAmount);
      remainingAmount -= applied;
      allocations.push({ stock, amount: applied });
    });
    return allocations;
  }, [amountValue, openStocks]);

  const filteredPayments = useMemo(() => {
    const from = paymentFilters.from;
    const to = paymentFilters.to;
    return payments.filter((payment) => {
      const paymentDate = toLocalDateKey(payment.paidAt || payment.createdAt);
      if (!paymentDate) return !from && !to;
      if (from && paymentDate < from) return false;
      if (to && paymentDate > to) return false;
      return true;
    });
  }, [paymentFilters.from, paymentFilters.to, payments]);

  const hasPaymentFilters = Boolean(paymentFilters.from || paymentFilters.to);

  if (providerLoading && !provider) {
    return (
      <div className="p-6">
        <InlineLoading />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="p-6 text-red-600">
        {providerError || "Proveedor no encontrado."}
      </div>
    );
  }

  const bankAccounts = Array.isArray(provider.ProviderBankAccounts)
    ? provider.ProviderBankAccounts
    : [];
  const totalPurchases = Number(summary?.totalPurchases || 0);
  const totalPaid = Number(summary?.totalPaid || 0);
  const balance = Number(summary?.balance || 0);
  const credit = Number(summary?.credit || 0);

  return (
    <>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">{provider.name}</h1>
            <p className="text-sm text-gray-600">
              Rut: {provider.rut ? formatRut(provider.rut) : "—"}
            </p>
          </div>
          <div className="flex gap-2">
            {canUpdateProvider && (
              <button
                onClick={() => navigate(`/branches/${branchId}/providers/${provider.id}/edit`)}
                className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              >
                Editar proveedor
              </button>
            )}
            <button
              onClick={() => navigate(`/branches/${branchId}/providers`)}
              className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
            >
              Volver
            </button>
          </div>
        </div>

        {providerError && <p className="text-red-600">{providerError}</p>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-sm text-gray-600">Total compras</p>
            <div className="text-xl font-semibold">
              {summaryLoading ? <InlineLoading /> : summary ? `$${formatMoney(totalPurchases)}` : "—"}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-sm text-gray-600">Total pagado</p>
            <div className="text-xl font-semibold">
              {summaryLoading ? <InlineLoading /> : summary ? `$${formatMoney(totalPaid)}` : "—"}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-sm text-gray-600">Saldo pendiente</p>
            <div className="text-xl font-semibold">
              {summaryLoading ? (
                <InlineLoading />
              ) : summary ? (
                `$${formatMoney(Math.max(balance, 0))}`
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-sm text-gray-600">Credito a favor</p>
            <div className="text-xl font-semibold">
              {summaryLoading ? <InlineLoading /> : summary ? `$${formatMoney(credit)}` : "—"}
            </div>
          </div>
        </div>

        {summaryError && <p className="text-sm text-red-600">{summaryError}</p>}

        <div className="rounded-lg bg-white p-4 shadow">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Historial de pagos</h2>
              <p className="text-sm text-gray-500">
                Pagos generales registrados para este proveedor.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="flex flex-col gap-1 text-sm text-gray-600">
                <span>Desde</span>
                <input
                  type="date"
                  value={paymentFilters.from}
                  onChange={(event) =>
                    setPaymentFilters((prev) => ({
                      ...prev,
                      from: event.target.value,
                    }))
                  }
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-600">
                <span>Hasta</span>
                <input
                  type="date"
                  value={paymentFilters.to}
                  onChange={(event) =>
                    setPaymentFilters((prev) => ({
                      ...prev,
                      to: event.target.value,
                    }))
                  }
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              {hasPaymentFilters && (
                <button
                  type="button"
                  onClick={() => setPaymentFilters({ from: "", to: "" })}
                  className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                >
                  Limpiar
                </button>
              )}
              <span className="text-sm text-gray-500 sm:pb-2">
                {paymentsLoading
                  ? "Cargando..."
                  : hasPaymentFilters
                    ? `${filteredPayments.length} de ${payments.length} pago(s)`
                    : `${payments.length} pago(s)`}
              </span>
            </div>
          </div>

          {paymentsError && <p className="mb-4 text-sm text-red-600">{paymentsError}</p>}

          {paymentsLoading ? (
            <p className="text-sm text-gray-600">
              <InlineLoading label="Cargando pagos" />
            </p>
          ) : payments.length === 0 ? (
            <p className="text-sm text-gray-600">Sin pagos registrados.</p>
          ) : filteredPayments.length === 0 ? (
            <p className="text-sm text-gray-600">No hay pagos en el rango seleccionado.</p>
          ) : (
            <ul className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {filteredPayments.map((payment) => {
                const allocations = payment.ProviderPaymentAllocations || [];
                const allocatedAmount = getAllocatedAmount(payment);
                const overpayAmount = Number(payment.overpayAmount || 0);
                const isExpanded = expandedPaymentIds.has(payment.id);

                return (
                  <li
                    key={payment.id}
                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Pago proveedor #{payment.id}</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          ${formatMoney(payment.amount)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Metodo: {getProviderPaymentMethodLabel(payment.method)}
                        </p>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <p>{formatDate(payment.paidAt || payment.createdAt)}</p>
                      </div>
                    </div>

                    <div className="mt-3 space-y-1 text-sm text-gray-700">
                      {payment.reference && <p>Referencia: {payment.reference}</p>}
                      {payment.note && <p>Nota: {payment.note}</p>}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-blue-100 px-2 py-1 text-blue-700">
                        Asignado: ${formatMoney(allocatedAmount)}
                      </span>
                      {overpayAmount > 0 && (
                        <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700">
                          Credito: ${formatMoney(overpayAmount)}
                        </span>
                      )}
                      {payment.voucherUrl && (
                        <a
                          href={payment.voucherUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full bg-slate-100 px-2 py-1 text-slate-700 hover:bg-slate-200"
                        >
                          Ver comprobante
                        </a>
                      )}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {allocations.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => handleTogglePaymentExpansion(payment.id)}
                          className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                        >
                          {isExpanded ? "Ocultar" : "Ver"} asignaciones ({allocations.length})
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500">
                          {overpayAmount > 0
                            ? "Pago sin asignaciones. El monto restante quedo como credito."
                            : "Sin asignaciones registradas."}
                        </p>
                      )}

                      {canManageExistingPayments && (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditingPayment(payment)}
                            className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePayment(payment)}
                            disabled={deletingPaymentId === payment.id}
                            className="rounded bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60"
                          >
                            {deletingPaymentId === payment.id ? (
                              <InlineLoading label="Eliminando" />
                            ) : (
                              "Eliminar"
                            )}
                          </button>
                        </>
                      )}
                    </div>

                    {isExpanded && allocations.length > 0 && (
                      <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                        {allocations.map((allocation) => {
                          const stock = allocation.Stock;
                          const stockList = stock?.StockList;
                          const stockListUrl = buildStockListUrl(stockList);

                          return (
                            <div
                              key={allocation.id}
                              className="rounded-lg border border-gray-200 bg-slate-50 p-3"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {stock?.varietyName || "Stock"}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Fecha stock: {formatDate(stock?.createdAt)}
                                  </p>
                                </div>
                                <p className="font-semibold text-gray-900">
                                  ${formatMoney(allocation.amount || 0)}
                                </p>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                <span className="rounded-full bg-white px-2 py-1 text-gray-700">
                                  Lista: {stockList?.name || "Sin lista"}
                                </span>
                                {stockList?.branchId && (
                                  <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-700">
                                    Sucursal #{stockList.branchId}
                                  </span>
                                )}
                                {stockListUrl && (
                                  <button
                                    type="button"
                                    onClick={() => navigate(stockListUrl)}
                                    className="rounded-full bg-blue-100 px-2 py-1 text-blue-700 hover:bg-blue-200"
                                  >
                                    Ver lista
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg bg-white p-4 shadow lg:col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Stocks pendientes</h2>
              <span className="text-sm text-gray-500">
                {openStocksLoading ? "Cargando..." : `${openStocks.length} item(s)`}
              </span>
            </div>

            {openStocksLoading ? (
              <p className="text-sm text-gray-600">
                <InlineLoading label="Cargando stocks" />
              </p>
            ) : openStocksError ? (
              <p className="text-sm text-red-600">{openStocksError}</p>
            ) : openStocks.length === 0 ? (
              <p className="text-sm text-gray-600">No hay stocks pendientes.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2">Fecha</th>
                      <th className="pb-2">Lista</th>
                      <th className="pb-2">Producto</th>
                      <th className="pb-2 text-right">Total</th>
                      <th className="pb-2 text-right">Pagado</th>
                      <th className="pb-2 text-right">Pendiente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openStocks.map((stock) => (
                      <tr key={stock.id} className="border-b last:border-b-0">
                        <td className="py-2">{formatDate(stock.createdAt)}</td>
                        <td className="py-2">{stock.StockList?.name || "—"}</td>
                        <td className="py-2">{stock.varietyName || "—"}</td>
                        <td className="py-2 text-right">
                          ${formatMoney(stock.totalAmount || 0)}
                        </td>
                        <td className="py-2 text-right">
                          ${formatMoney(stock.paidAmount || 0)}
                        </td>
                        <td className="py-2 text-right font-semibold">
                          ${formatMoney(stock.balance || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-lg bg-white p-4 shadow">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Cuentas bancarias</h2>
                <span className="text-sm text-gray-500">{bankAccounts.length} cuenta(s)</span>
              </div>
              {bankAccounts.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Sin cuentas bancarias registradas. Edita el proveedor para agregarlas.
                </p>
              ) : (
                <div className="space-y-3">
                  {bankAccounts.map((account) => (
                    <div key={account.id} className="rounded-lg border border-gray-200 p-3">
                      <p className="font-medium text-gray-900">{account.bankName || "Banco"}</p>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        <p>Cuenta: {account.bankAccount || "—"}</p>
                        <p>Tipo: {account.bankAccountType || "—"}</p>
                        <p>
                          RUT titular: {account.bankRut ? formatRut(account.bankRut) : "—"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4 rounded-lg bg-white p-4 shadow">
              <h2 className="text-lg font-semibold">Pagar deuda</h2>
              <div className="space-y-1 text-sm text-gray-600">
                <p>
                  Saldo actual:{" "}
                  {summaryLoading ? (
                    <InlineLoading />
                  ) : summary ? (
                    `$${formatMoney(Math.max(balance, 0))}`
                  ) : (
                    "—"
                  )}
                </p>
                <p>Restante despues de pago: ${formatMoney(remaining)}</p>
                {overpay > 0 && (
                  <p className="text-red-600">Sobrepago: ${formatMoney(overpay)}</p>
                )}
              </div>

              {canUpdateProvider ? (
                <>
                  {paymentError && <p className="text-sm text-red-600">{paymentError}</p>}
                  <div className="space-y-2">
                    <input
                      type="number"
                      placeholder="Monto"
                      value={paymentForm.amount}
                      onChange={(event) => handlePaymentChange("amount", event.target.value)}
                      className="w-full rounded border border-gray-300 p-2"
                      min="1"
                      max={PROVIDER_PAYMENT_MAX_AMOUNT}
                      step="1"
                    />
                    <p className="text-xs text-gray-500">
                      Monto maximo: ${formatPesoAmount(PROVIDER_PAYMENT_MAX_AMOUNT)}
                    </p>
                    <select
                      value={paymentForm.method}
                      onChange={(event) => handlePaymentChange("method", event.target.value)}
                      className="w-full rounded border border-gray-300 p-2"
                    >
                      <option value="">Metodo</option>
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="date"
                      value={paymentForm.paidAt}
                      onChange={(event) => handlePaymentChange("paidAt", event.target.value)}
                      className="w-full rounded border border-gray-300 p-2"
                    />
                    <input
                      type="text"
                      placeholder="Referencia (opcional)"
                      value={paymentForm.reference}
                      onChange={(event) => handlePaymentChange("reference", event.target.value)}
                      className="w-full rounded border border-gray-300 p-2"
                      maxLength={PROVIDER_TEXT_MAX_LENGTH}
                    />
                    <textarea
                      rows="3"
                      placeholder="Nota (opcional)"
                      value={paymentForm.note}
                      onChange={(event) => handlePaymentChange("note", event.target.value)}
                      className="w-full rounded border border-gray-300 p-2"
                      maxLength={PROVIDER_TEXT_MAX_LENGTH}
                    />
                    <div className="space-y-1">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(event) =>
                          handlePaymentChange("voucherFile", event.target.files?.[0] || null)
                        }
                        className="w-full rounded border border-gray-300 p-2"
                      />
                      {paymentForm.voucherFile && (
                        <p className="text-xs text-gray-500">{paymentForm.voucherFile.name}</p>
                      )}
                    </div>
                    <button
                      onClick={handleSubmitPayment}
                      disabled={submitting}
                      className="w-full rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {submitting ? <InlineLoading label="Guardando" /> : "Registrar pago"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No tienes permiso para registrar pagos.
                </p>
              )}

              {openStocksError && (
                <p className="text-xs text-amber-700">
                  No se pudo cargar la distribucion estimada de este pago.
                </p>
              )}

              {allocationPreview.length > 0 && (
                <div className="border-t border-gray-200 pt-2">
                  <h3 className="mb-2 text-sm font-semibold">Distribucion estimada</h3>
                  <div className="space-y-2 text-sm">
                    {allocationPreview.map((item) => (
                      <div key={item.stock.id} className="flex justify-between gap-3">
                        <span className="text-gray-600">
                          {item.stock.varietyName || "Stock"} ({formatDate(item.stock.createdAt)})
                        </span>
                        <span className="font-semibold">${formatMoney(item.amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ProviderPaymentModal
        payment={editingPayment}
        isOpen={Boolean(editingPayment)}
        onClose={() => setEditingPayment(null)}
        onSave={handleSavePayment}
      />
    </>
  );
};

export default ProviderDetailPage;
